import React, { useEffect, useState } from 'react';
import api from '../api';
import { 
    ClipboardList, FileCheck, Truck, Users, 
    CheckCircle2, XCircle, AlertTriangle, Upload, 
    Plus, Edit, Trash2, Calendar, FileText, 
    ArrowLeft, User, Mail, PenTool, Check, X
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const MyCompany = () => {
    // --- STATE ---
    const [activeTab, setActiveTab] = useState('docs'); 
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Data States
    const [companyInfo, setCompanyInfo] = useState(null); 
    const [docs, setDocs] = useState([]);
    const [requirements, setRequirements] = useState([]);
    const [equip, setEquip] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Document Form State
    const [newDoc, setNewDoc] = useState({ type: '', title: '', file: null, expiry: '' });
    
    // Equipment Form State (Create)
    const [newTool, setNewTool] = useState({ 
        name: '', description: '', 
        last_inspection: '', next_inspection: '', 
        last_service: '', next_service: '', 
        photo: null 
    });

    // Equipment Edit State (Update)
    const [editingTool, setEditingTool] = useState(null); 

    // Document Upload for Equipment State
    const [uploadingForToolId, setUploadingForToolId] = useState(null);
    const [toolDoc, setToolDoc] = useState({ title: '', file: null, expiry: '' });

    // Employee Form State
    const [newEmp, setNewEmp] = useState({ first_name: '', last_name: '', role: '' });
    const [showAddForm, setShowAddForm] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- DATA LOADING ---
    const loadData = async () => {
        setLoading(true);
        try {
            const userRes = await api.get('users/me/');
            const compListRes = await api.get('companies/');
            const myComp = compListRes.data.find(c => c.name === userRes.data.company);
            setCompanyInfo(myComp);

            const [reqRes, docRes, equipRes, empRes] = await Promise.all([
                api.get('config/docs/'),
                api.get('contractor/documents/'),
                api.get('contractor/equipment/'),
                api.get('employees/roster/')
            ]);
            
            setRequirements(reqRes.data);
            if (reqRes.data.length > 0 && !newDoc.type) setNewDoc(prev => ({ ...prev, type: reqRes.data[0].name }));
            
            setDocs(docRes.data);
            setEquip(equipRes.data);
            setEmployees(empRes.data);

        } catch (err) { console.error("Error loading data", err); } 
        finally { setLoading(false); }
    };

    // --- HANDLERS ---
    const handleUpload = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('doc_type', newDoc.type);
        formData.append('title', newDoc.title || newDoc.type);
        formData.append('file', newDoc.file);
        if (newDoc.expiry) formData.append('expiry_date', newDoc.expiry);

        try {
            await api.post('contractor/documents/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("Document Uploaded Successfully");
            setNewDoc({ ...newDoc, title: '', file: null, expiry: '' });
            const res = await api.get('contractor/documents/');
            setDocs(res.data);
        } catch (err) { alert("Upload failed"); }
    };

    const handleAddTool = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', newTool.name);
        formData.append('description', newTool.description);
        if (newTool.last_inspection) formData.append('last_inspection_date', newTool.last_inspection);
        if (newTool.next_inspection) formData.append('next_inspection_due', newTool.next_inspection);
        if (newTool.last_service) formData.append('last_service_date', newTool.last_service);
        if (newTool.next_service) formData.append('next_service_date', newTool.next_service);
        if (newTool.photo) formData.append('photo', newTool.photo);

        try {
            await api.post('contractor/equipment/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("Tool Added Successfully! Please upload documents now.");
            setNewTool({ name: '', description: '', last_inspection: '', next_inspection: '', last_service: '', next_service: '', photo: null });
            const res = await api.get('contractor/equipment/');
            setEquip(res.data);
        } catch (err) { alert("Failed to add tool"); }
    };

    // --- NEW: UPDATE TOOL HANDLER ---
    const handleUpdateTool = async (e) => {
        e.preventDefault();
        try {
            // We use PATCH to update specific fields
            await api.patch(`contractor/equipment/${editingTool.id}/`, {
                description: editingTool.description,
                last_inspection_date: editingTool.last_inspection_date || null,
                next_inspection_due: editingTool.next_inspection_due || null,
                last_service_date: editingTool.last_service_date || null,
                next_service_date: editingTool.next_service_date || null,
            });
            alert("Equipment Updated Successfully");
            setEditingTool(null); // Close modal
            const res = await api.get('contractor/equipment/');
            setEquip(res.data);
        } catch (err) { alert("Update failed"); }
    };

    const handleUploadToolDoc = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', toolDoc.title);
        formData.append('file', toolDoc.file);
        if (toolDoc.expiry) formData.append('expiry_date', toolDoc.expiry);

        try {
            await api.post(`contractor/equipment/${uploadingForToolId}/upload-doc/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("Document attached successfully");
            setToolDoc({ title: '', file: null, expiry: '' });
            setUploadingForToolId(null);
            const res = await api.get('contractor/equipment/');
            setEquip(res.data);
        } catch (err) { alert("Failed to upload document"); }
    };

    const handleDeleteToolDoc = async (toolId, docId) => {
        if(!window.confirm("Delete this document?")) return;
        try {
            await api.delete(`contractor/equipment/${toolId}/delete-doc/${docId}/`);
            const res = await api.get('contractor/equipment/');
            setEquip(res.data);
        } catch (err) { alert("Delete failed"); }
    };

    const handleDeleteTool = async (id) => { if(window.confirm("Remove equipment completely?")) { await api.delete(`contractor/equipment/${id}/`); setEquip(prev => prev.filter(e => e.id !== id)); }};

    const handleAddEmployee = async (e) => {
        e.preventDefault();
        try { 
            await api.post('employees/', { ...newEmp, is_inducted: false }); 
            alert("Employee Added Successfully"); 
            setNewEmp({ first_name: '', last_name: '', role: '' }); 
            setShowAddForm(false); 
            const res = await api.get('employees/roster/'); 
            setEmployees(res.data); 
        } catch (err) { alert("Error adding employee"); }
    };

    const handleDeleteDoc = async (id) => { if(window.confirm("Delete?")) { await api.delete(`contractor/documents/${id}/`); setDocs(prev => prev.filter(d => d.id !== id)); }};
    const handleDeleteEmployee = async (id) => { if(window.confirm("Delete record?")) { await api.delete(`employees/${id}/`); setEmployees(prev => prev.filter(e => e.id !== id)); }};

    // --- UI HELPERS ---
    const getMatchingDoc = (reqName) => docs.find(d => d.doc_type === reqName);
    
    const renderStatusBadge = (status) => {
        if (status === 'APPROVED' || status === 'OPERATIONAL') return <span style={{color: THEME.success, fontWeight:'bold', fontSize:'0.75rem', background:'rgba(16, 185, 129, 0.1)', padding:'2px 8px', borderRadius:'12px', border:`1px solid ${THEME.success}`, display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle2 size={12}/> {status === 'OPERATIONAL' ? 'Active' : 'Approved'}</span>;
        if (status === 'REJECTED') return <span style={{color: THEME.danger, fontWeight:'bold', fontSize:'0.75rem', background:'rgba(239, 68, 68, 0.1)', padding:'2px 8px', borderRadius:'12px', border:`1px solid ${THEME.danger}`, display:'flex', alignItems:'center', gap:'4px'}}><XCircle size={12}/> Rejected</span>;
        if (status === 'MAINTENANCE') return <span style={{color: THEME.warning, fontWeight:'bold', fontSize:'0.75rem', background:'rgba(250, 204, 21, 0.1)', padding:'2px 8px', borderRadius:'12px', border:`1px solid ${THEME.warning}`, display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={12}/> Maintenance</span>;
        return <span style={{color: '#94a3b8', fontWeight:'bold', fontSize:'0.75rem', background:'rgba(148, 163, 184, 0.1)', padding:'2px 8px', borderRadius:'12px', border:`1px solid ${THEME.border}`, display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={12}/> Pending</span>;
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', marginBottom:'10px', boxSizing:'border-box' };

    if (loading) return <div style={{color:'white'}}>Loading...</div>;

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{marginBottom: '30px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent:'space-between', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'20px', gap: isMobile ? '15px' : '0'}}>
                <h2 style={{margin: 0, color: 'white'}}>{companyInfo ? companyInfo.name : 'My Company'}</h2>
                {companyInfo && (
                    <div style={{
                        background: companyInfo.readiness === 'GREEN' ? 'rgba(16, 185, 129, 0.2)' : companyInfo.readiness === 'YELLOW' ? 'rgba(250, 204, 21, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: companyInfo.readiness === 'GREEN' ? THEME.success : companyInfo.readiness === 'YELLOW' ? THEME.warning : THEME.danger,
                        border: `1px solid ${companyInfo.readiness === 'GREEN' ? THEME.success : companyInfo.readiness === 'YELLOW' ? THEME.warning : THEME.danger}`,
                        padding: '6px 16px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem', display:'flex', alignItems:'center', gap:'6px'
                    }}>
                        {companyInfo.readiness === 'GREEN' ? <><CheckCircle2 size={16}/> Fully Compliant</> : companyInfo.readiness === 'YELLOW' ? <><AlertTriangle size={16}/> Pending Review</> : <><XCircle size={16}/> Non-Compliant</>}
                    </div>
                )}
            </div>

            {/* TABS */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '25px', background:'#020617', padding:'5px', borderRadius:'8px', border:`1px solid ${THEME.border}`, width: isMobile ? '100%' : 'fit-content', overflowX: 'auto'}}>
                {[
                    { id: 'docs', label: 'Compliance', icon: FileCheck },
                    { id: 'equip', label: 'Equipment', icon: Truck },
                    { id: 'staff', label: 'Employees', icon: Users }
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                        padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, whiteSpace: 'nowrap',
                        background: activeTab === tab.id ? THEME.primary : 'transparent', color: activeTab === tab.id ? 'white' : THEME.textSec, transition: 'all 0.2s', flex: isMobile ? 1 : 'none',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                        <tab.icon size={16}/> {tab.label}
                    </button>
                ))}
            </div>

            <div className="card" style={{minHeight:'400px', background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding: isMobile ? '15px' : '20px', overflow:'hidden'}}>
                
                {/* 1. DOCUMENTS */}
                {activeTab === 'docs' && (
                    <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'30px'}}>
                        <div>
                            <h3 style={{marginTop:0, color:'white', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'10px'}}>Required Documents</h3>
                            <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                {requirements.map(req => {
                                    const doc = getMatchingDoc(req.name);
                                    return (
                                        <div key={req.id} style={{padding:'15px', background:'rgba(255,255,255,0.02)', borderRadius:'8px', borderBottom:`1px solid ${THEME.border}`, display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '10px'}}>
                                            <div>
                                                <div style={{color:'white', fontWeight:'500'}}>{req.name}</div>
                                                {req.is_required && <span style={{fontSize:'0.7rem', color:THEME.danger, fontWeight:'bold', background:'rgba(239, 68, 68, 0.1)', padding:'2px 6px', borderRadius:'4px', marginTop:'4px', display:'inline-block'}}>REQUIRED</span>}
                                                {doc && <div style={{fontSize:'0.8rem', color:THEME.textSec, marginTop:'4px'}}>Exp: {doc.expiry_date || 'N/A'}</div>}
                                            </div>
                                            
                                            {doc ? (
                                                <div style={{display:'flex', gap:'10px', alignItems:'center', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end'}}>
                                                    {renderStatusBadge(doc.status)}
                                                    <a href={doc.file} target="_blank" rel="noreferrer" style={{color:THEME.primary, fontSize:'0.85rem', fontWeight:'bold', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px'}}><FileText size={14}/> View</a>
                                                    
                                                    <button onClick={() => handleDeleteDoc(doc.id)} style={{background:'transparent', border:'none', cursor:'pointer', color:THEME.textSec}}><Trash2 size={16}/></button>
                                                </div>
                                            ) : (
                                                <span style={{fontSize:'0.8rem', color:THEME.textSec, fontStyle:'italic'}}>Not uploaded</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{background: '#020617', padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`, height:'fit-content'}}>
                            <h4 style={{color: 'white', marginTop: 0, display:'flex', alignItems:'center', gap:'8px'}}><Upload size={18}/> Upload Document</h4>
                            <form onSubmit={handleUpload}>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Type</label>
                                <select value={newDoc.type} onChange={e => setNewDoc({...newDoc, type: e.target.value})} style={inputStyle}>
                                    {requirements.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                    <option value="OTHER">Other</option>
                                </select>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Title</label>
                                <input type="text" placeholder="e.g. 2025 Renewal" value={newDoc.title} onChange={e => setNewDoc({...newDoc, title: e.target.value})} style={inputStyle} />
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Expiry Date</label>
                                <input type="date" value={newDoc.expiry} onChange={e => setNewDoc({...newDoc, expiry: e.target.value})} style={inputStyle} />
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>File</label>
                                <input type="file" onChange={e => setNewDoc({...newDoc, file: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} required />
                                <button type="submit" style={{width:'100%', padding:'10px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Submit Document</button>
                            </form>
                        </div>
                    </div>
                )}

                {/* 2. EQUIPMENT */}
                {activeTab === 'equip' && (
                    <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'30px', width: '100%'}}>
                        {/* LIST */}
                        <div style={{minWidth: 0}}>
                            <h3 style={{marginTop:0, color:'white'}}>My Equipment</h3>
                            {equip.map(e => (
                                <div key={e.id} style={{background:'#020617', padding:'15px', marginBottom:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                    <div style={{display:'flex', gap:'15px', alignItems:'flex-start', flexDirection: isMobile ? 'column' : 'row'}}>
                                        <div style={{width: isMobile ? '100%' : '80px', height: isMobile ? '150px' : '80px', background:'#1e293b', borderRadius:'6px', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center'}}>
                                            {e.photo ? <img src={e.photo} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="Equip" /> : <Truck size={32} color={THEME.textSec}/>}
                                        </div>

                                        <div style={{flex:1, width: '100%'}}>
                                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                                                <div>
                                                    <div style={{color:'white', fontWeight:'bold', fontSize:'1.1rem'}}>{e.name}</div>
                                                    <div style={{color:THEME.textSec}}>{e.description}</div>
                                                </div>
                                                {renderStatusBadge(e.status)}
                                            </div>

                                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'10px', fontSize:'0.85rem'}}>
                                                <div style={{color:THEME.textSec}}>Next Insp: <span style={{color: new Date(e.next_inspection_due) < new Date() ? THEME.danger : 'white'}}>{e.next_inspection_due || '-'}</span></div>
                                                <div style={{color:THEME.textSec}}>Next Service: <span style={{color: new Date(e.next_service_date) < new Date() ? THEME.warning : 'white'}}>{e.next_service_date || '-'}</span></div>
                                            </div>

                                            {/* Documents */}
                                            <div style={{marginTop:'15px'}}>
                                                <div style={{fontSize:'0.8rem', color:THEME.textSec, marginBottom:'5px'}}>Documents:</div>
                                                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                                                    {e.documents.length === 0 ? <span style={{color:THEME.textSec, fontSize:'0.8rem', fontStyle:'italic'}}>No documents uploaded.</span> : e.documents.map(d => (
                                                        <a key={d.id} href={d.file} target="_blank" rel="noreferrer" style={{textDecoration:'none', background:THEME.card, border:`1px solid ${THEME.border}`, padding:'4px 10px', borderRadius:'4px', color:THEME.primary, fontSize:'0.8rem'}}>
                                                            📄 {d.title}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Quick Actions */}
                                            <div style={{marginTop:'15px', paddingTop:'15px', borderTop:`1px dashed ${THEME.border}`, display:'flex', gap:'10px', justifyContent:'flex-end'}}>
                                               <button 
                                                    onClick={() => setUploadingForToolId(e.id)} 
                                                    style={{
                                                        background:'transparent', 
                                                        border:`1px solid ${THEME.primary}`, 
                                                        color:THEME.primary, 
                                                        fontSize:'0.8rem', 
                                                        padding:'4px 8px', 
                                                        borderRadius:'4px', 
                                                        cursor:'pointer', 
                                                        display:'flex', 
                                                        alignItems:'center', 
                                                        gap:'4px'
                                                    }}
                                                >
                                                    <Upload size={12}/> Add Doc
                                                </button>
                                                <button onClick={() => setEditingTool(e)} style={{background:'transparent', border:`1px solid ${THEME.textSec}`, color:THEME.textSec, fontSize:'0.8rem', padding:'4px 8px', borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}><Edit size={12}/> Edit</button>
                                                <button onClick={() => handleDeleteTool(e.id)} style={{background:'transparent', border:'none', color:THEME.danger, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px'}}><Trash2 size={12}/> Remove</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {equip.length === 0 && <div style={{color:THEME.textSec, textAlign:'center', padding:'20px'}}>No equipment found.</div>}
                        </div>

                        {/* ADD FORM */}
                        <div style={{background: '#020617', padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`, height:'fit-content'}}>
                            <h4 style={{color: 'white', marginTop: 0, display:'flex', alignItems:'center', gap:'8px'}}><Plus size={18}/> Add Equipment</h4>
                            <form onSubmit={handleAddTool}>
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Tool Name</label>
                                <input type="text" placeholder="e.g. Excavator 01" value={newTool.name} onChange={e => setNewTool({...newTool, name: e.target.value})} style={inputStyle} required />
                                
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label>
                                <textarea rows="2" placeholder="Model, Serial..." value={newTool.description} onChange={e => setNewTool({...newTool, description: e.target.value})} style={inputStyle} />
                                
                                <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                    <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Insp.</label><input type="date" value={newTool.last_inspection} onChange={e => setNewTool({...newTool, last_inspection: e.target.value})} style={inputStyle} /></div>
                                    <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Insp.</label><input type="date" value={newTool.next_inspection} onChange={e => setNewTool({...newTool, next_inspection: e.target.value})} style={inputStyle} /></div>
                                </div>
                                <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                    <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Service</label><input type="date" value={newTool.last_service} onChange={e => setNewTool({...newTool, last_service: e.target.value})} style={inputStyle} /></div>
                                    <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Service</label><input type="date" value={newTool.next_service} onChange={e => setNewTool({...newTool, next_service: e.target.value})} style={inputStyle} /></div>
                                </div>
                                
                                <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Photo</label>
                                <input type="file" accept="image/*" onChange={e => setNewTool({...newTool, photo: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} />
                                
                                <button type="submit" style={{width:'100%', padding:'10px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Add Item</button>
                            </form>
                        </div>
                    </div>
                )}

                {/* 3. STAFF */}
                {activeTab === 'staff' && (
                    <div>
                        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
                            <h3 style={{margin:0, color:'white'}}>Employee Roster</h3>
                            <button onClick={() => setShowAddForm(!showAddForm)} style={{background: THEME.primary, color:'white', border:'none', padding:'6px 16px', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><Plus size={16}/> Add Worker</button>
                        </div>
                        
                        {showAddForm && (
                            <div style={{background:'#020617', padding:'20px', borderRadius:'8px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                                <form onSubmit={handleAddEmployee} style={{display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:'10px', alignItems: isMobile ? 'stretch' : 'end'}}>
                                    <div style={{flex:1}}><label style={{display:'block', fontSize:'0.8rem', color:THEME.textSec, marginBottom:'5px'}}>First Name</label><input placeholder="John" value={newEmp.first_name} onChange={e => setNewEmp({...newEmp, first_name: e.target.value})} style={inputStyle} required /></div>
                                    <div style={{flex:1}}><label style={{display:'block', fontSize:'0.8rem', color:THEME.textSec, marginBottom:'5px'}}>Last Name</label><input placeholder="Doe" value={newEmp.last_name} onChange={e => setNewEmp({...newEmp, last_name: e.target.value})} style={inputStyle} required /></div>
                                    <div style={{flex:1}}><label style={{display:'block', fontSize:'0.8rem', color:THEME.textSec, marginBottom:'5px'}}>Role</label><input placeholder="Welder" value={newEmp.role} onChange={e => setNewEmp({...newEmp, role: e.target.value})} style={inputStyle} required /></div>
                                    <button type="submit" style={{background: THEME.success, color:'white', border:'none', borderRadius:'6px', padding:'10px 20px', fontWeight:'bold', cursor:'pointer', height:'38px', marginBottom:'2px'}}>Save</button>
                                </form>
                            </div>
                        )}

                        <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap:'15px'}}>
                            {employees.map(emp => (
                                <div key={emp.id} style={{background:'#020617', padding:'15px', borderRadius:'8px', border: emp.is_banned ? `1px solid ${THEME.danger}` : `1px solid ${THEME.border}`, position:'relative'}}>
                                    <div style={{fontWeight:'bold', color:'white'}}>{emp.full_name}</div>
                                    <div style={{fontSize:'0.85rem', color:THEME.textSec}}>{emp.role}</div>
                                    <div style={{marginTop:'10px', fontSize:'0.8rem', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                        {emp.is_banned ? <span style={{color:THEME.danger, fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px'}}><XCircle size={12}/> BANNED</span> : (emp.is_inducted ? <span style={{color:THEME.success, display:'flex', alignItems:'center', gap:'4px'}}><CheckCircle2 size={12}/> Inducted</span> : <span style={{color:THEME.warning, display:'flex', alignItems:'center', gap:'4px'}}><AlertTriangle size={12}/> Pending</span>)}
                                        <div style={{display:'flex', gap:'5px'}}>
                                            {emp.type !== 'Account Holder' && (
                                                <>
                                                    <button onClick={() => handleDeleteEmployee(emp.id)} style={{background:'transparent', border:'none', cursor:'pointer', color:THEME.textSec}}><Trash2 size={16}/></button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* BAN MODAL */}
            {/* Note: In "MyCompany", contractors generally can't ban their own people (admins do that). 
                If you want them to be able to "remove" (delete), that logic is above. 
                I'm keeping the modal hidden/removed for now unless specified. */}

            {/* MODAL FOR UPLOADING DOC TO TOOL */}
            {uploadingForToolId && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width: isMobile ? '90%' : '400px', border:`1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color:'white'}}>Attach Document</h4>
                        <form onSubmit={handleUploadToolDoc}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Title</label>
                            <input placeholder="e.g. Safety Cert" value={toolDoc.title} onChange={e => setToolDoc({...toolDoc, title: e.target.value})} style={inputStyle} required />
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Expiry (Optional)</label>
                            <input type="date" value={toolDoc.expiry} onChange={e => setToolDoc({...toolDoc, expiry: e.target.value})} style={inputStyle} />
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>File</label>
                            <input type="file" onChange={e => setToolDoc({...toolDoc, file: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} required />
                            <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                                <button type="button" onClick={() => setUploadingForToolId(null)} style={{flex:1, background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Cancel</button>
                                <button type="submit" style={{flex:1, background:THEME.success, border:'none', color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Upload</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL FOR EDITING TOOL (NEW) */}
            {editingTool && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width: isMobile ? '90%' : '500px', border:`1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color:'white'}}>Edit Equipment: {editingTool.name}</h4>
                        <form onSubmit={handleUpdateTool}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label>
                            <textarea rows="2" value={editingTool.description || ''} onChange={e => setEditingTool({...editingTool, description: e.target.value})} style={inputStyle} />
                            
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Insp.</label><input type="date" value={editingTool.last_inspection_date || ''} onChange={e => setEditingTool({...editingTool, last_inspection_date: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Insp.</label><input type="date" value={editingTool.next_inspection_due || ''} onChange={e => setEditingTool({...editingTool, next_inspection_due: e.target.value})} style={inputStyle} /></div>
                            </div>
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Service</label><input type="date" value={editingTool.last_service_date || ''} onChange={e => setEditingTool({...editingTool, last_service_date: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Service</label><input type="date" value={editingTool.next_service_date || ''} onChange={e => setEditingTool({...editingTool, next_service_date: e.target.value})} style={inputStyle} /></div>
                            </div>

                            <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                                <button type="button" onClick={() => setEditingTool(null)} style={{flex:1, background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Cancel</button>
                                <button type="submit" style={{flex:1, background:THEME.success, border:'none', color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyCompany;